import React, { useState } from "react";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useApp } from "@/context/AppContext";
import { CART } from "@/constants/testIds";
import api from "@/lib/api";
import { toast } from "sonner";

const CartDrawer = ({ open, onClose }) => {
  const { cart, updateQty, removeItem, cartTotal } = useApp();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!cart.length) return;
    setLoading(true);
    try {
      const items = cart.map((i) => ({ sku: i.sku, quantity: i.quantity }));
      const res = await api.post("/payments/checkout", {
        items,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        data-testid={CART.drawer}
        className="w-full sm:max-w-md bg-background border-l border-border p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-5 border-b border-border">
          <SheetTitle className="font-serif text-2xl tracking-tight text-left">Your Sacred Basket</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cart.length === 0 ? (
            <div data-testid={CART.empty} className="py-16 text-center">
              <div className="text-secondary text-4xl mb-4">✦</div>
              <p className="text-sm text-muted-foreground uppercase tracking-label">Basket is empty</p>
              <p className="mt-3 text-sm">Add sacred items to begin.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {cart.map((item) => (
                <div key={item.sku} data-testid={CART.item(item.sku)} className="flex gap-4 pb-5 border-b border-border last:border-0">
                  <img src={item.image} alt={item.name} className="w-20 h-20 object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium leading-snug">{item.name}</p>
                      <button data-testid={CART.removeBtn(item.sku)} onClick={() => removeItem(item.sku)} className="text-muted-foreground hover:text-primary">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">${item.price.toFixed(2)}</p>
                    <div className="mt-3 flex items-center gap-2">
                      <button data-testid={CART.qtyDec(item.sku)} onClick={() => updateQty(item.sku, item.quantity - 1)} className="p-1 border border-border hover:bg-muted">
                        <Minus className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                      <span className="text-sm w-6 text-center">{item.quantity}</span>
                      <button data-testid={CART.qtyInc(item.sku)} onClick={() => updateQty(item.sku, item.quantity + 1)} className="p-1 border border-border hover:bg-muted">
                        <Plus className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-border px-6 py-5 space-y-4">
            <div className="flex justify-between items-baseline">
              <span className="text-xs uppercase tracking-label text-muted-foreground">Subtotal</span>
              <span data-testid={CART.total} className="font-serif text-2xl">${cartTotal.toFixed(2)}</span>
            </div>
            <button
              onClick={checkout}
              disabled={loading}
              data-testid={CART.checkoutBtn}
              className="w-full bg-primary text-primary-foreground py-3 text-xs uppercase tracking-label font-bold hover:bg-primary/90 luxe-btn disabled:opacity-50"
            >
              {loading ? "Redirecting…" : "Proceed to Payment"}
            </button>
            <p className="text-[10px] text-center text-muted-foreground">Secure checkout via Stripe. Test card: 4242 4242 4242 4242</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
